import { forwardRef } from 'react';
import { Icon } from '../Icon';
import type { IconSize } from '../Icon';
import { cn } from '@/utils/cn';

export interface LoadingIconProps {
  /**
   * Size of the loading icon — inherits the same scale as the Primer Icon
   * component. Defaults to 'medium'.
   * small = 12px, medium = 16px, large = 24px, xl = 40px.
   */
  size?: IconSize;
  /**
   * Additional Tailwind classes. Use `text-*` to control colour via
   * currentColor — the icon inherits the parent's text colour by default.
   */
  className?: string;
  /** Accessible label for the loading state. Defaults to 'Loading'. */
  'aria-label'?: string;
}

/**
 * Primer LoadingIcon component.
 *
 * A clockwise-spinning loading indicator built on top of the Primer `Icon`
 * component. Uses the `loading` icon from the sprite sheet — 8 notches with
 * graduated opacity (100% at 12 o'clock, decreasing by 10% clockwise to 30%
 * at 10:30) — giving a clear sense of rotational direction.
 *
 * Colour is always inherited via `currentColor`. Place a Tailwind `text-*`
 * class on this component or a parent to control it.
 *
 * The spin animation uses a custom `animate-spin-stepped` utility — a
 * stepped rotation that advances 45° at a time, giving a clear sense of
 * rotational direction rather than a smooth continuous spin.
 *
 * @example
 * // Default — inherits parent text colour, medium size
 * <LoadingIcon />
 *
 * @example
 * // Large, white — inside a dark button
 * <LoadingIcon size="large" className="text-white" />
 */
export const LoadingIcon = forwardRef<SVGSVGElement, LoadingIconProps>(function LoadingIcon(
  { size = 'medium', className, 'aria-label': ariaLabel = 'Loading' },
  ref
) {
  return (
    <Icon
      ref={ref}
      name="loading"
      size={size}
      aria-label={ariaLabel}
      aria-hidden={undefined}
      role="status"
      className={cn('animate-spin-stepped', className)}
    />
  );
});
